import React, { useState } from 'react';
import './AdminPermissionsPage.scss';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import SimpleBar from 'simplebar-react';
import {
  getSuperadmins,
  updateSuperadminPermissions,
} from '../../../shared/services/admin/permissions/AdminPermissionsService';
import {
  AccessMode,
  AdminPermissions,
  AdminResource,
  COSTS_GRANTOR_USER_ID,
  MANAGED_RESOURCES,
  SuperadminWithPermissions,
} from '../../../shared/models/admin/permissions/AdminPermission';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';

type ManagedResource = (typeof MANAGED_RESOURCES)[number];

// Compact access badge: R/W (read+write) in green, R (read only) in blue, — (none) muted.
const accessBadge = (permissions: AdminPermissions, resource: AdminResource, hasWrite: boolean) => {
  const access = permissions[resource];
  if (!access?.read) {
    return <span className="admin-permissions__badge admin-permissions__badge--none">—</span>;
  }
  const isWrite = hasWrite && access.write;
  return (
    <span className={`admin-permissions__badge admin-permissions__badge--${isWrite ? 'rw' : 'r'}`}>
      {isWrite ? 'R/W' : 'R'}
    </span>
  );
};

const fullName = (row: SuperadminWithPermissions) =>
  `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();

// Two-letter initials from the name; with no name, from the email's local part split on separators
// (jan.kowalski@… → JK, admin@… → AD).
const initials = (row: SuperadminWithPermissions) => {
  const fromName = `${row.firstName?.[0] ?? ''}${row.lastName?.[0] ?? ''}`;
  if (fromName) return fromName.toUpperCase();

  const local = row.email?.split('@')[0] ?? '';
  const parts = local.split(/[._\-+]/).filter(Boolean);
  const fromEmail = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : local.slice(0, 2);
  return fromEmail.toUpperCase() || '?';
};

const userCell = (row: SuperadminWithPermissions) => (
  <div className="admin-permissions__user">
    <span className="admin-permissions__avatar">{initials(row)}</span>
    <span className="admin-permissions__name-box">
      <span className="admin-permissions__name">{fullName(row) || row.email || '—'}</span>
      {/* Tylko telefon (karta bez osobnej kolumny Email) — na desktopie schowany w stylach. */}
      {fullName(row) && row.email && <span className="admin-permissions__email">{row.email}</span>}
    </span>
  </div>
);

const AdminPermissionsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { accountResponse, refetchAccountSilently } = useAccount();
  const { data: superadmins = [], isLoading } = useQuery({
    queryKey: ['admin-superadmins'],
    queryFn: getSuperadmins,
  });

  const [editing, setEditing] = useState<SuperadminWithPermissions | null>(null);
  const [form, setForm] = useState<AdminPermissions | null>(null);
  const [visible, setVisible] = useState(false);

  // Costs is handed out by one person; everyone else neither sees nor can save that toggle (the backend
  // keeps the stored Costs flags for them anyway).
  const visibleResources =
    accountResponse?.userId === COSTS_GRANTOR_USER_ID
      ? MANAGED_RESOURCES
      : MANAGED_RESOURCES.filter(({ resource }) => resource !== AdminResource.COSTS);

  // You may strip yourself of anything except Permissions — dropping that locks you out of this tab.
  const isSelf = !!editing && editing.userId === accountResponse?.userId;
  const isLocked = (resource: AdminResource, mode: AccessMode) =>
    isSelf &&
    resource === AdminResource.PERMISSIONS &&
    !!editing?.permissions[AdminResource.PERMISSIONS]?.[mode];

  const mutation = useMutation({
    mutationFn: ({ userId, permissions }: { userId: number; permissions: AdminPermissions }) =>
      updateSuperadminPermissions(userId, permissions),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-superadmins'] });
      // Your own permissions drive the admin tab list (AdminPage) and the route guards, both read from
      // the cached account response — refetch it so a tab you just granted yourself shows up now.
      if (variables.userId === accountResponse?.userId) {
        await refetchAccountSilently();
      }
      closeDialog();
    },
  });

  const openEdit = (row: SuperadminWithPermissions) => {
    setEditing(row);
    setForm({ ...row.permissions });
    setVisible(true);
  };

  // Only toggle visibility on close so the content stays mounted through the close animation
  // (clearing editing/form mid-animation is what made the dialog jump).
  const closeDialog = () => setVisible(false);

  const toggle = (resource: AdminResource, mode: AccessMode, checked: boolean) =>
    setForm((prev) => {
      if (!prev) return prev;
      const access = { ...prev[resource], [mode]: checked };
      // Write requires read: turning read off also clears write.
      if (mode === 'read' && !checked) access.write = false;
      return { ...prev, [resource]: access };
    });

  const save = () => {
    if (editing && form) {
      mutation.mutate({ userId: editing.userId, permissions: form });
    }
  };

  const dialogFooter = (
    <div className="admin-permissions__footer">
      <button className="btn btn--secondary btn--sm" onClick={closeDialog} type="button">
        Cancel
      </button>
      <button className="btn btn--sm" onClick={save} disabled={mutation.isPending} type="button">
        Save changes
      </button>
    </div>
  );

  // Telefon: karty zamiast tabeli przewijanej w bok (64rem) — użytkownik jako tytuł, edycja w rogu,
  // sekcje w siatce po trzy.
  const table = (
          <DataTable
            value={superadmins}
            loading={isLoading}
            scrollable={!isMobile}
            tableStyle={isMobile ? undefined : { minWidth: '64rem', tableLayout: 'auto' }}
            tableClassName="table"
            className="table-wrapper table-wrapper--stack admin-permissions__table"
            responsiveLayout="stack"
            stripedRows
            removableSort
          >
        <Column
          header="User"
          headerClassName="table__header"
          bodyClassName="table__cell table__cell--stack-title"
          body={userCell}
        />
        <Column
          field="email"
          header="Email"
          headerClassName="table__header"
          bodyClassName="table__cell table__cell--nowrap"
          hidden={isMobile}
          sortable
        />
        {visibleResources.map(({ resource, label, hasWrite }) => (
          <Column
            key={resource}
            header={label}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '120px' }}
            body={(row: SuperadminWithPermissions) => accessBadge(row.permissions, resource, hasWrite)}
          />
        ))}
        <Column
          header=""
          frozen={!isMobile}
          alignFrozen="right"
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell table__cell--center table__cell--stack-actions"
          style={{ width: '64px' }}
          body={(row: SuperadminWithPermissions) => (
            <button
              className="action-btn"
              onClick={() => openEdit(row)}
              type="button"
              aria-label="Edit permissions"
            >
              <span className="ico ico--edit ico--sm" />
            </button>
          )}
        />
          </DataTable>
  );

  return (
    <div className="admin-permissions">
      <div className="admin-permissions__table-frame">
        {isMobile ? table : <SimpleBar autoHide={false}>{table}</SimpleBar>}
      </div>

      <div className="admin-permissions__legend">
        <span className="admin-permissions__legend-title">Permission legend</span>
        <div className="admin-permissions__legend-items">
          <span className="admin-permissions__legend-item">
            <span className="admin-permissions__badge admin-permissions__badge--rw">R/W</span>
            <span className="admin-permissions__legend-text">
              <span className="admin-permissions__legend-label">Read &amp; write</span>
              <span className="admin-permissions__legend-desc">Can view and edit this section</span>
            </span>
          </span>
          <span className="admin-permissions__legend-item">
            <span className="admin-permissions__badge admin-permissions__badge--r">R</span>
            <span className="admin-permissions__legend-text">
              <span className="admin-permissions__legend-label">Read only</span>
              <span className="admin-permissions__legend-desc">Can view but not edit</span>
            </span>
          </span>
          <span className="admin-permissions__legend-item">
            <span className="admin-permissions__badge admin-permissions__badge--none">—</span>
            <span className="admin-permissions__legend-text">
              <span className="admin-permissions__legend-label">No access</span>
              <span className="admin-permissions__legend-desc">Section is hidden</span>
            </span>
          </span>
        </div>
      </div>

      <Dialog
        header={editing ? `Permissions for ${editing.email}` : ''}
        visible={visible}
        onHide={closeDialog}
        footer={form ? dialogFooter : undefined}
        className="dialog-box admin-permissions__dialog"
      >
        {form && (
          <div className="admin-permissions__form">
            <p className="admin-permissions__hint">
              {isSelf
                ? 'These are your own permissions — you cannot take away your own Permissions access.'
                : 'Choose which sections this admin can view and edit.'}
            </p>
            <DataTable
              value={visibleResources}
              tableClassName="table"
              className="table-wrapper"
              stripedRows
            >
              <Column
                header="Section"
                headerClassName="table__header"
                bodyClassName="table__cell"
                body={(r: ManagedResource) => r.label}
              />
              <Column
                header="Read"
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell table__cell--center"
                style={{ width: '96px' }}
                body={(r: ManagedResource) => (
                  <Checkbox
                    checked={form[r.resource].read}
                    disabled={isLocked(r.resource, 'read')}
                    onChange={(e) => toggle(r.resource, 'read', !!e.checked)}
                  />
                )}
              />
              <Column
                header="Write"
                headerClassName="table__header table__header--center admin-permissions__col-write"
                bodyClassName="table__cell table__cell--center admin-permissions__col-write"
                style={{ width: '96px' }}
                body={(r: ManagedResource) =>
                  r.hasWrite ? (
                    <Checkbox
                      checked={form[r.resource].write}
                      disabled={!form[r.resource].read || isLocked(r.resource, 'write')}
                      onChange={(e) => toggle(r.resource, 'write', !!e.checked)}
                    />
                  ) : null
                }
              />
            </DataTable>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AdminPermissionsPage;
